import path from 'node:path';
import { clientFetch } from '~/lib/.client/fetch';
import type { SnapshotInfo } from '~/lib/.server/database/snapshots';
import { chatHooks } from '~/lib/chat-hooks';
import type { File, FileMap, Folder } from '~/lib/stores/files';
import { extractBoltArtifactTitle } from '~/utils/artifacts';
import { addWorkDir, removeWorkDir } from '~/utils/workDir';

export type FileSystemSnapshot = Record<
  string,
  Pick<Folder, 'type'> | (Pick<File, 'type' | 'content' | 'blobId'> & { isBinary?: boolean })
>;

interface SnapshotFullInfo extends SnapshotInfo {
  finalized?: boolean;
}

export class Snapshot {
  readonly createdAt: Date;

  id?: string;
  projectId: string;
  messageId: string;
  isFinalized: boolean;
  title: string | undefined;
  bookmarkedAt: Date | null | undefined;

  saving = Promise.resolve();

  constructor(projectId: string, info: SnapshotFullInfo) {
    this.id = info.id;
    this.projectId = projectId;
    this.messageId = info.messageId;
    this.isFinalized = info.finalized ?? false;
    this.createdAt = info.createdAt;
    this.title = info.title;
    this.bookmarkedAt = info.bookmarkedAt;
  }

  get isPublished() {
    return false;
  }

  get isBookmarked() {
    return !!this.bookmarkedAt;
  }

  get displayTitle() {
    return (
      this.title ||
      extractBoltArtifactTitle(
        chatHooks.messages
          .get()
          .find((item) => item.id === this.messageId)
          ?.parts.filter((item) => item.type === 'text')
          .map((item) => item.text)
          .join('') ?? '',
      ) ||
      'Untitled'
    );
  }

  async finalize(files: Readonly<FileMap>) {
    if (this.isFinalized) {
      return;
    }

    this.isFinalized = true;

    const filesystem: FileSystemSnapshot = {};

    const directories = new Set<string>();

    for (const [filePath, dirent] of Object.entries(files)) {
      if (!dirent) {
        continue;
      }

      const filename = removeWorkDir(filePath);

      if (dirent.type === 'folder') {
        // we only add directories if they are empty
        if (!directories.has(filename)) {
          filesystem[filename] = {
            type: dirent.type,
          };
        }
      } else {
        const dirname = path.dirname(filename);

        directories.add(dirname);

        filesystem[filename] = {
          type: dirent.type,
          content: dirent.content,
          ...(dirent.isBinary && { isBinary: true }),
          ...(dirent.blobId && { blobId: dirent.blobId }),
        };

        if (filesystem[dirname] && filesystem[dirname].type === 'folder') {
          // we delete non-empty directories
          delete filesystem[dirname];
        }
      }
    }

    const saveSnapshot = async (count = 1) => {
      try {
        const body = {
          id: this.messageId,
          snapshot: JSON.stringify(filesystem),
          title: this.title,
        };

        const response = await clientFetch(`/api/snapshot/${this.projectId}`, {
          method: 'POST',
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error('Failed to save snapshot');
        }

        const snapshotInfo = await response.json<{
          id: string;
          messageId: string;
          title?: string;
          createdAt: string;
          bookmarkedAt: string | null;
        }>();

        this.id = snapshotInfo.id;

        if (snapshotInfo.title) {
          this.title = snapshotInfo.title;
        }
      } catch (error) {
        if (count >= 5) {
          // stop retrying after 5 attempts
          throw error;
        }

        setTimeout(() => {
          saveSnapshot(count + 1);
        }, count * 500);
      }
    };

    this.saving = saveSnapshot();

    await this.saving;
  }

  /**
   * This function does not produce a valid FileMap because it's missing the folders containing files.
   * This is fine when using `forkProject` but not with the `files` from the store as the UI needs them.
   */
  async getAsFileMap(): Promise<FileMap> {
    const snapshot = await this.fetch();
    const files: FileMap = {};

    for (const [filePath, fileEntry] of Object.entries(snapshot)) {
      if (fileEntry.type === 'folder') {
        files[addWorkDir(filePath)] = {
          type: 'folder',
          isLocked: false,
          isTargeted: false,
        };
      } else {
        files[addWorkDir(filePath)] = {
          ...fileEntry,
          isLocked: false,
          isTargeted: false,
        };
      }
    }

    return files;
  }

  async fetch() {
    const response = await clientFetch(`/api/snapshot/${this.projectId}/${this.messageId}`);

    if (!response.ok) {
      throw new Error('Failed to load snapshot');
    }

    const { snapshot } = await response.json<{ snapshot: FileSystemSnapshot }>();

    return snapshot;
  }
}
